import { useState } from 'react'

import { api } from '../../api/client'
import type { AccessVerifica } from '../../api/types'
import { useAsync } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import { Card, ErrorBanner, Spinner, formatDateTime } from '../common'
import { DataTable } from '../DataTable'
import { VistaSigilli } from './Sigilli'

/**
 * «Registro dell'area»: the register about the register. The chain of
 * integrity and its verification; who has access to the area and the role
 * changes from the audit log (the segregation is only as good as that list);
 * who looked at what. Open to the reviewer: checking is reading.
 */

export function EsitoVerifica({ v }: { v: AccessVerifica | null | undefined }) {
  if (!v) return <span className="muted">{t('mai verificata')}</span>
  const sigilli = v.seals
  return v.ok
    ? <span className="badge badge--ok">{t('integra · {n} eventi controllati', { n: v.checked })}{sigilli && sigilli.seals > 0 ? ` · ${t('{n} sigilli validi, {m} con marca temporale', { n: sigilli.valid, m: sigilli.stamped })}` : ''}</span>
    : <span className="badge badge--error">{t('rotta all’evento {seq}: {motivo}', { seq: v.break?.seq ?? '?', motivo: v.break?.reason ?? '' })}</span>
}

export function VistaRegistro({ scrive = false }: { scrive?: boolean }) {
  const dati = useAsync(() => api.accessLedger(), [])
  const [verifica, setVerifica] = useState<AccessVerifica | null>(null)
  const [busy, setBusy] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)

  async function verifica_() {
    setBusy(true)
    setErrore(null)
    try {
      setVerifica(await api.accessVerify())
      void dati.reload()
    } catch (e) {
      setErrore(e)
    } finally {
      setBusy(false)
    }
  }

  const d = dati.data
  if (dati.loading && !d) return <Spinner />
  if (!d) return <ErrorBanner error={dati.error} />
  const ultima = verifica ?? d.last_verification
  return (
    <>
      <Card title={t('Catena di integrità')}>
        <p className="field__hint" style={{ marginTop: 0 }}>
          {t('Ogni evento porta l’impronta SHA-256 del precedente: togliere, cambiare o inserire un evento rompe la catena da quel punto in poi. Il database rifiuta modifiche, svuotamenti e cancellazioni prima della fine della conservazione. La verifica ricalcola tutta la catena, controlla i sigilli e chiede alla sonda d’archivio di rileggere la sua copia.')}
        </p>
        <dl className="kv kv--attributi">
          <dt>{t('Eventi nel registro')}</dt><dd>{d.events.toLocaleString()}</dd>
          <dt>{t('Ultimo evento')}</dt><dd className="mono">{d.head_seq} · {d.head_hash.slice(0, 24)}…</dd>
          <dt>{t('Ultima verifica')}</dt><dd><EsitoVerifica v={ultima} />{ultima && 'at' in ultima && ultima.at && <span className="muted"> · {formatDateTime(ultima.at)}{ultima.actor ? ` · ${ultima.actor}` : ''}</span>}</dd>
        </dl>
        <ErrorBanner error={errore} />
        <button type="button" onClick={() => void verifica_()} disabled={busy}>{busy ? t('Verifico…') : t('Verifica adesso')}</button>
        {d.chain_log.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <DataTable rows={d.chain_log} rowKey={(r) => `${r.at}-${r.kind}`} conteggio={{ nome: t('voci') }} paginazione={{ predefinita: 10 }} columns={[
              { key: 'at', header: t('Quando'), type: 'date', value: (r) => r.at, render: (r) => formatDateTime(r.at) },
              { key: 'kind', header: t('Cosa'), filter: 'select', value: (r) => (r.kind === 'retention' ? t('conservazione') : t('verifica')) },
              { key: 'esito', header: t('Esito'), value: (r) => (r.kind === 'retention' ? t('{n} eventi rimossi fino al {seq}', { n: r.count, seq: r.up_to_seq ?? 0 }) : r.ok ? t('integra ({n} eventi)', { n: r.count }) : t('rotta')) },
              { key: 'actor', header: t('Chi'), value: (r) => r.actor ?? '' },
            ]} />
          </div>
        )}
      </Card>

      <VistaSigilli scrive={scrive} />

      <Card title={t('Chi ha accesso a quest’area')}>
        <p className="field__hint" style={{ marginTop: 0 }}>
          {t('Il registro lo aprono gli amministratori del tenant e della piattaforma e i ruoli che lo nominano; un ruolo che vede «tutto» no. Chi gestisce gli utenti può assegnarsi uno di questi ruoli: per questo l’elenco e le modifiche dei ruoli sono qui, sotto gli occhi del revisore.')}
        </p>
        <DataTable rows={d.members} rowKey={(m) => m.user_id} conteggio={{ nome: t('utenti') }} columns={[
          { key: 'email', header: t('Utente'), value: (m) => m.email, render: (m) => <>{m.name ?? m.email}{m.name && <><br /><small className="muted">{m.email}</small></>}</> },
          { key: 'role', header: t('Ruolo'), filter: 'select', value: (m) => m.role === 'tenant_admin' ? t('Amministratore del tenant') : m.role },
          { key: 'level', header: t('Livello'), filter: 'select', value: (m) => (m.level === 'write' ? t('governa') : t('consulta')) },
          { key: 'status', header: t('Stato'), filter: 'select', value: (m) => m.status },
        ]} />
        {d.role_changes.length > 0 && (
          <>
            <h4 className="form__sezione">{t('Modifiche di utenti e ruoli (dal registro di audit)')}</h4>
            <DataTable rows={d.role_changes} rowKey={(r) => `${r.at}-${r.target}`} conteggio={{ nome: t('modifiche') }} paginazione={{ predefinita: 10 }} columns={[
              { key: 'at', header: t('Quando'), type: 'date', value: (r) => r.at, render: (r) => formatDateTime(r.at) },
              { key: 'actor', header: t('Chi'), value: (r) => r.actor },
              { key: 'action', header: t('Cosa'), value: (r) => r.action },
              { key: 'target', header: t('Su'), mono: true, value: (r) => r.target ?? '' },
            ]} />
          </>
        )}
      </Card>

      <Card title={t('Chi ha consultato il registro')}>
        <DataTable rows={d.views} rowKey={(v) => `${v.at}-${v.actor}-${v.what}`} conteggio={{ nome: t('consultazioni') }} initialSort={{ key: 'at', dir: 'desc' }} columns={[
          { key: 'at', header: t('Quando'), type: 'date', value: (v) => v.at, render: (v) => formatDateTime(v.at) },
          { key: 'actor', header: t('Chi'), value: (v) => v.actor_name ?? v.actor },
          { key: 'action', header: t('Azione'), filter: 'select', value: (v) => ({ view: t('lettura'), search: t('ricerca'), export: t('esportazione'), verify: t('verifica'), report: t('report') } as Record<string, string>)[v.action] ?? v.action },
          { key: 'what', header: t('Cosa'), value: (v) => v.what },
          { key: 'detail', header: t('Filtri'), value: (v) => Object.entries(v.detail).map(([k, x]) => `${k}=${String(x)}`).join(' '), hidden: true },
          { key: 'source_ip', header: t('Da'), mono: true, value: (v) => v.source_ip ?? '' },
        ]} />
      </Card>
    </>
  )
}
